import React, { useState, useEffect } from 'react';
import { Modal } from '@appkit4/react-components/modal';
import { Input } from '@appkit4/react-components/field';
import { Button } from '@appkit4/react-components/button';
import DropDown, { DropDownOption } from '../../components/reusables/DropDown';
import Icon from '../../components/reusables/Icon';
import { Keyword } from './KeywordAnalysisModal';

interface CreateSplitRuleModalProps {
    visible: boolean;
    onClose: () => void;
    onSave: (values: SplitRuleValues) => void;
    selectedKeyword: Keyword | null;
}

export type SplitRuleValues = {
    splitRuleName: string;
    description: string;
    priority: string;
    ruleType: string;
    keyword: string;
    poolAssignments: PoolAssignment[];
};

type PoolAssignment = {
    id: number;
    poolCode: string;
    percentage: string;
};

const ruleTypeOptions: DropDownOption[] = [
    { value: 'fixed_asset', label: 'Fixed Asset' },
    { value: 'project_data', label: 'Project Data' },
];

const defaultPoolAssignments: PoolAssignment[] = [
    { id: 1, poolCode: 'MP', percentage: '' },
    { id: 2, poolCode: 'NQ', percentage: '' },
    { id: 3, poolCode: 'FYA', percentage: '' },
    { id: 4, poolCode: 'MP', percentage: '' },
    { id: 5, poolCode: 'MP', percentage: '' },
    { id: 6, poolCode: 'MP', percentage: '' },
    { id: 7, poolCode: 'MP', percentage: '' },
];

const AddRuleModal: React.FC<CreateSplitRuleModalProps> = ({
    visible,
    onClose,
    onSave,
    selectedKeyword,
}) => {
    const [splitRuleName, setSplitRuleName] = useState('');
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState('');
    const [ruleType, setRuleType] = useState('');
    const [keyword, setKeyword] = useState('');
    const [poolAssignments, setPoolAssignments] = useState<PoolAssignment[]>(
        defaultPoolAssignments
    );

    useEffect(() => {
        if (visible && selectedKeyword) {
            setKeyword(selectedKeyword.keyword);
        }
    }, [visible, selectedKeyword]);

    const handlePoolPercentageChange = (id: number, percentage: string) => {
        setPoolAssignments((prev) =>
            prev.map((p) => (p.id === id ? { ...p, percentage } : p))
        );
    };

    const handleResetKeyword = () => {
        setKeyword('');
    };

    const handleSave = () => {
        onSave({
            splitRuleName,
            description,
            priority,
            ruleType,
            keyword,
            poolAssignments,
        });
        resetForm();
        onClose();
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const resetForm = () => {
        setSplitRuleName('');
        setDescription('');
        setPriority('');
        setRuleType('');
        setKeyword('');
        setPoolAssignments(
            defaultPoolAssignments.map((p) => ({ ...p, percentage: '' }))
        );
    };

    return (
        <Modal
            visible={visible}
            title="New Split Rule"
            onCancel={handleClose}
            modalStyle={{ width: '600px' }}
            footer={
                <div className="create-split-rule__footer">
                    <Button kind="primary" onClick={handleSave}>
                        Create Rule
                    </Button>
                </div>
            }
        >
            <div className="create-split-rule">
                <div className="create-split-rule__row">
                    <div className="create-split-rule__field">
                        <Input
                            title="Split rule Name"
                            value={splitRuleName}
                            onChange={(value: string) =>
                                setSplitRuleName(value)
                            }
                        />
                    </div>
                    <div className="create-split-rule__field">
                        <Input
                            title="Description"
                            value={description}
                            onChange={(value: string) => setDescription(value)}
                            required
                        />
                    </div>
                </div>

                <div className="create-split-rule__row">
                    <div className="create-split-rule__field">
                        <Input
                            title="Split rule priority"
                            value={priority}
                            onChange={(value: string) => setPriority(value)}
                            required
                        />
                    </div>
                    <div className="create-split-rule__field">
                        <DropDown
                            data={ruleTypeOptions}
                            placeholder="Rule Type"
                            value={ruleType}
                            onSelect={(value) => setRuleType(value as string)}
                            required
                        />
                    </div>
                </div>

                <div className="create-split-rule__section-label">
                    <span className="create-split-rule__section-title">
                        Rule search keywords
                        <span className="create-split-rule__required">*</span>
                    </span>
                    <span className="create-split-rule__sub-label">
                        in Asset Description
                    </span>
                </div>

                <div className="create-split-rule__keyword-row">
                    <div className="create-split-rule__keyword-input">
                        <Input
                            title="Keywords"
                            value={keyword}
                            onChange={(value: string) => setKeyword(value)}
                        />
                    </div>
                    <button
                        type="button"
                        className="create-split-rule__keyword-delete"
                        onClick={handleResetKeyword}
                    >
                        <Icon iconName="Appkit4-icon icon-delete-outline" />
                    </button>
                </div>

                <div className="create-split-rule__section-label">
                    <span className="create-split-rule__section-title">
                        Assigns to pools in these proportions
                        <span className="create-split-rule__required">*</span>
                    </span>
                </div>

                <div className="create-split-rule__pools">
                    {poolAssignments.map((p) => (
                        <div className="create-split-rule__pool-row" key={p.id}>
                            <div className="create-split-rule__pool-icon">
                                <Icon iconName="Appkit4-icon icon-archive-outline" />
                            </div>
                            <div className="create-split-rule__pool-label">
                                {p.poolCode}
                            </div>
                            <div className="create-split-rule__pool-input">
                                <Input
                                    value={p.percentage}
                                    onChange={(value: string) =>
                                        handlePoolPercentageChange(p.id, value)
                                    }
                                    placeholder=""
                                />
                            </div>
                            <span className="create-split-rule__pool-percent">
                                %
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </Modal>
    );
};

export default AddRuleModal;
